import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Draft, PatientDraftsRepositoryService } from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientDraftsStore — open drafts (reports / scripts / comments / emails /
 * messages) for the current patient.
 *
 * Auto-loads on patient change. No push subscription — drafts are
 * created/deleted within this client and the local DraftsService event
 * stream triggers reloads from consumers. Exposes delete() for the
 * drafts list page.
 *
 * `count` is a computed convenience over items, replacing the legacy
 * separate totalDrafts$ subject (-1 sentinel becomes 0 here).
 */
@Injectable({
  providedIn: 'root'
})
export class PatientDraftsStore {
  private patientStore = inject(PatientStore);
  private repo = inject(PatientDraftsRepositoryService);

  private readonly _items = signal<Draft[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }
      this.fetch(patient.id);
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Delete a draft. Optimistic — removes from the list immediately and
   * rolls back on error.
   */
  delete(draft: Draft): Observable<boolean> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientDraftsStore.delete() called with no current patient');
    }
    const previous = this._items();
    this._items.set(previous.filter(d => d.draftId !== draft.draftId || d.draftType !== draft.draftType));
    return this.repo.delete(patient.id, draft).pipe(
      tap({
        error: error => {
          this._items.set(previous);
          
        }
      })
    );
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadAll(patientId).subscribe({
      next: drafts => {
        this._items.set(drafts);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient drafts');
        
      }
    });
  }
}
